import { memo, useCallback, useMemo, useState, ChangeEvent } from 'react'
import { GS, GS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import LoaderMini from 'components/common/loaders/mini'

import texts from '../texts'
import { formatInvoiceMonth } from '../helpers'
import {
  Panel,
  Notice,
  EmptyState,
  FilterBar,
  Field,
  Select,
  PrimaryButton,
  SubHeading,
} from '../Styles'

// Stable key for an invoice (year/month). Month is 1-based from the backend.
const invoiceKey = (inv: GS_m.AnalyticsInvoiceRef): string => `${inv.year ?? 0}-${inv.month ?? 0}`

// Task 15.5 — Invoices tab (Requirements 9.2, 9.4, 9.6, 9.7).
// Lists invoices newest-first; selecting one displays its document reference.
// Handles empty (disabled control + "no invoices available") and error states.
const Invoices = () => {
  const { t } = useTranslation(texts)

  const {
    data: invoicesData,
    error: invoicesError,
    isLoading: invoicesLoading,
  } = GS.useWalletInvoices()

  // The list is already ordered newest-first by the backend (Requirement 9.3);
  // sort defensively so the UI is robust regardless of response ordering.
  const invoices = useMemo(() => {
    const list = invoicesData?.invoices ?? []
    return [...list].sort((a, b) => {
      const ay = a.year ?? 0
      const by = b.year ?? 0
      if (ay !== by) return by - ay
      return (b.month ?? 0) - (a.month ?? 0)
    })
  }, [invoicesData])

  const [selectedKey, setSelectedKey] = useState<string>('')

  const selectedInvoice = useMemo(() => invoices.find(inv => invoiceKey(inv) === selectedKey), [
    invoices,
    selectedKey,
  ])

  const hasInvoices = invoices.length > 0
  const docParams: GS_m.GetWalletInvoiceDocumentParams = {
    year: selectedInvoice?.year ?? 0,
    month: selectedInvoice?.month ?? 0,
  }

  // Only fetch the document once a valid invoice is selected (9.2).
  const {
    data: docData,
    error: docError,
    isLoading: docLoading,
  } = GS.useWalletInvoiceDocument(docParams, { enabled: Boolean(selectedInvoice) })

  const handleSelect = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedKey(e.target.value)
  }, [])

  const openDocument = useCallback(() => {
    const reference = docData?.reference
    if (reference) {
      window.open(reference, '_blank', 'noopener,noreferrer')
    }
  }, [docData])

  // Error retrieving the invoice list — show an error in place of the list (9.6).
  if (invoicesError) {
    return (
      <Panel>
        <Notice variant="error" data-cy="invoicesError">
          {t('errorLoading')}
        </Notice>
      </Panel>
    )
  }

  if (invoicesLoading) {
    return (
      <Panel>
        <LoaderMini center />
      </Panel>
    )
  }

  // Empty: prevent selection + show "no invoices available" (Requirement 9.4).
  if (!hasInvoices) {
    return (
      <Panel>
        <FilterBar>
          <Field>
            {t('invoiceMonth')}
            <Select value="" disabled data-cy="invoiceSelect">
              <option value="">{t('selectInvoice')}</option>
            </Select>
          </Field>
        </FilterBar>
        <EmptyState data-cy="noInvoices">{t('noInvoices')}</EmptyState>
      </Panel>
    )
  }

  return (
    <Panel>
      <FilterBar>
        <Field>
          {t('invoiceMonth')}
          <Select value={selectedKey} onChange={handleSelect} data-cy="invoiceSelect">
            <option value="">{t('selectInvoice')}</option>
            {invoices.map(inv => (
              <option key={invoiceKey(inv)} value={invoiceKey(inv)}>
                {formatInvoiceMonth(inv.year, inv.month)}
              </option>
            ))}
          </Select>
        </Field>
      </FilterBar>

      {selectedInvoice && (
        <div data-cy="invoiceDocument">
          {docLoading && <LoaderMini center />}

          {/* Error retrieving the selected document (Requirement 9.7). */}
          {docError && (
            <Notice variant="error" data-cy="invoiceDocumentError">
              {t('errorLoading')}
            </Notice>
          )}

          {!docLoading && !docError && docData?.reference && (
            <>
              <SubHeading>
                {t('invoiceReference')}: {docData.reference}
              </SubHeading>
              <PrimaryButton type="button" onClick={openDocument} data-cy="openInvoiceDocument">
                {t('openDocument')}
              </PrimaryButton>
            </>
          )}
        </div>
      )}
    </Panel>
  )
}

Invoices.whyDidYouRender = true

export default memo(Invoices)
